import {
  $recipientCreateStore,
  $recipientUpdateStore,
  $transferRequestsCreateStore,
  $transferRequestsDeleteFileStore,
  $transferRequestsUpdateStore,
  $transferRequestsUploadFileStore,
} from '@lib/store';
import { AccountResponse, AmountTypeEnum, RecipientResponse, TransferRecipientTypeEnum, TransferRequestTypeEnum } from '@shared/api';
import type { AccountFormData, AddressFormData, CompanyFormData, PaymentFormData, PersonalFormData } from '@widgets';
import type { CustomFile, FileWithId } from '@shared/widgets';

type CreateRecipientData = { type: TransferRecipientTypeEnum; vopForceUpdate?: boolean } & (PersonalFormData | CompanyFormData) &
  AddressFormData &
  AccountFormData;
type UpdateRecipientData = { id: string } & CreateRecipientData;

type CreateTransfer = {
  account: AccountResponse;
  recipient: RecipientResponse;
  payment: { type: TransferRequestTypeEnum } & PaymentFormData;
};
type UpdateTransfer = CreateTransfer & { payment: { id: string } };

export class PaymentUtils {
  static createRecipient = async (data: CreateRecipientData) => {
    let request = {};
    if (data.type === TransferRecipientTypeEnum.Individual && 'firstName' in data)
      request = { firstName: data.firstName, lastName: data.lastName, idNumber: data.idNumber };
    if (data.type === TransferRecipientTypeEnum.Business && 'companyName' in data) request = { companyName: data.companyName };

    const result = await $recipientCreateStore.events.createRecipient({
      ...request,
      type: data.type,
      countryCode: data.companyCountry,
      currency: data.currency,
      accountNumber: data.accountNumber,
      accountType: data.accountType!,
      iban: data.iban,
      bicOrSwiftCode: data.bic,
      bankName: data.bankName,
      bankCountryCode: data.bankCountry || null,
      bankProvince: data.bankProvince || null,
      bankAddress: data.bankAddress || null,
      bankCity: data.bankCity || null,
      branchCode: data.branchCode || null,
      email: data.email,
      address: data.address || null,
      postCode: data.postCode || null,
      city: data.city || null,
      province: data.province || null,
      vopForceUpdate: data.vopForceUpdate,
    });
    return result.data;
  };
  static updateRecipient = async (data: UpdateRecipientData) => {
    let request = {};
    if (data.type === TransferRecipientTypeEnum.Individual && 'firstName' in data)
      request = { firstName: data.firstName, lastName: data.lastName, idNumber: data.idNumber };
    if (data.type === TransferRecipientTypeEnum.Business && 'companyName' in data) request = { companyName: data.companyName };

    const result = await $recipientUpdateStore.events.updateRecipient({
      ...request,
      id: data.id,
      type: data.type,
      countryCode: data.companyCountry,
      currency: data.currency,
      accountNumber: data.accountNumber,
      accountType: data.accountType!,
      iban: data.iban,
      bicOrSwiftCode: data.bic,
      bankName: data.bankName,
      email: data.email,
      bankProvince: data.bankProvince || null,
      bankAddress: data.bankAddress || null,
      bankCity: data.bankCity || null,
      branchCode: data.branchCode || null,
      bankCountryCode: data.bankCountry || null,
      address: data.address || null,
      postCode: data.postCode || null,
      city: data.city || null,
      province: data.province || null,
      vopForceUpdate: data.vopForceUpdate,
    });
    return result.data;
  };

  static createTransfer = async ({ account, recipient, payment }: CreateTransfer) => {
    const result = await $transferRequestsCreateStore.events.createTransfer({
      type: payment.type,
      fromAccountId: account.id,
      beneficiaryCountryCode: recipient.countryCode,
      beneficiaryCurrency: recipient.currency,
      recipientId: recipient.id,
      senderAmount: payment.amount!,
      totalAmount: payment.amount!,
      beneficiaryAmount: payment.beneficiaryAmount!,
      reasonOfThePayment: payment.paymentReason,
      purpose: payment.purpose,
      fastPayment: payment.fastPayment,
      StickToBeneficiaryAmount: payment.amountType === AmountTypeEnum.BeneficiaryAmount,
    });
    return result;
  };
  static updateTransfer = async ({ account, recipient, payment }: UpdateTransfer) => {
    const result = await $transferRequestsUpdateStore.events.updateTransfer({
      id: payment.id,
      type: payment.type,
      fromAccountId: account.id,
      beneficiaryCountryCode: recipient.countryCode,
      beneficiaryCurrency: recipient.currency,
      recipientId: recipient.id,
      senderAmount: payment.amount!,
      totalAmount: payment.amount!,
      beneficiaryAmount: payment.beneficiaryAmount!,
      reasonOfThePayment: payment.paymentReason,
      purpose: payment.purpose,
      fastPayment: payment.fastPayment,
      StickToBeneficiaryAmount: payment.amountType === AmountTypeEnum.BeneficiaryAmount,
    });
    return result;
  };

  static uploadPaymentDocuments = async (data: { transferRequestId: string; files: (FileWithId | CustomFile)[] }) => {
    const result = [];

    for (const file of data.files) {
      if ('file' in file) {
        result.push(file);
        continue;
      }

      const response = await $transferRequestsUploadFileStore.events.createDocument({ transferRequestId: data.transferRequestId, file });
      const uploadedFile = { id: response.data || '', name: file.name, type: file.type, file };
      result.push(uploadedFile);
    }

    return result;
  };
  static deletePaymentDocument = async (data: { transferRequestId?: string; files: (FileWithId | CustomFile)[]; file: FileWithId | CustomFile }) => {
    if (data.transferRequestId && data.file.id)
      await $transferRequestsDeleteFileStore.events.update({ TransferRequestId: data.transferRequestId, DocumentId: data.file.id });

    return data.files.filter((file) => {
      if (data.file.id) return 'id' in file ? file.id !== data.file.id : true;
      return file.name !== data.file.name;
    });
  };
}
